import { useSyncExternalStore } from 'react';

/**
 * Thème (US-24). §4 exige « thème sombre disponible » ; l'application suivait
 * uniquement `prefers-color-scheme`, sans aucun réglage — suivre l'OS sans
 * recours n'est pas une disponibilité (audit externe, constat 22). En cuisine
 * le besoin est réel et changeant : plan de travail très éclairé le jour,
 * pièce tamisée le soir, et l'écran reste allumé en continu pendant qu'on
 * cuisine.
 *
 * Trois positions, `system` par défaut — ne rien imposer à qui n'a pas d'avis.
 */

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'mlc:theme';
const CHANGE_EVENT = 'mlc:theme-change';

function isPreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function readThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/**
 * Applique la préférence au document. `system` **retire** l'attribut plutôt
 * que d'écrire une valeur : la feuille de style repose sur la requête média
 * `prefers-color-scheme`, qui doit reprendre la main telle quelle.
 *
 * `color-scheme` est posé en même temps pour que les contrôles natifs
 * restants (cases à cocher, ascenseurs) suivent le thème choisi et non celui
 * du système.
 */
export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') {
    root.removeAttribute('data-theme');
    root.style.removeProperty('color-scheme');
    return;
  }
  root.setAttribute('data-theme', preference);
  root.style.setProperty('color-scheme', preference);
}

export function writeThemePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Réglage de confort : une écriture refusée ne doit rien casser.
  }
  applyTheme(preference);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onStoreChange);
  window.addEventListener('storage', onStoreChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onStoreChange);
    window.removeEventListener('storage', onStoreChange);
  };
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, readThemePreference, () => 'system');
}
